import React, { createElement as _createElement } from 'react';

/**
 * A class to manage the current list of dialogs to render
 */
class Manager {
  constructor() {
    this.dialogs = [];
    this.listeners = [];
    this.nextId = 0;
  }

  /**
   * Returns true if a node in the manager has the given id.
   */
  isShowing(id) {
    if (id == null) {
      return false;
    }
    return this.dialogs.some(item => item.id === id);
  }

  /**
   * Adds a dialog to the render pool
   */
  add(dialog) {
    const {
      id,
      removeDelay = 500,
      component,
      onClose: closeCallback,
      props = {}
    } = dialog;
    if (this.isShowing(id)) {
      return false;
    }
    const key = `DLG-${this.nextId++}`;
    const onClose = event => {
      this.remove(key);
      if (closeCallback) {
        closeCallback(event, dialog);
      }
    };
    const entry = {
      id,
      key,
      removeDelay,
      onClose,
      show: true,
      props,
      component
    };
    this.dialogs.push(entry);
    this.fireEvent({
      type: 'add',
      dialogs: this.dialogs,
      entry
    });
    return onClose;
  }
  getById(id) {
    return this.dialogs.find(d => d.id === id);
  }
  removeById(id) {
    const entry = this.getById(id);
    if (entry) {
      entry.onClose();
      return true;
    }
    return false;
  }
  removeAll() {
    this.dialogs.forEach(dialog => {
      dialog.onClose();
    });
  }
  remove(key) {
    const idx = this.dialogs.findIndex(d => d.key === key);
    if (idx >= 0) {
      const entry = this.dialogs[idx];
      if (entry.removeDelay <= 0) {
        this.dialogs.splice(idx, 1);
      } else {
        setTimeout(() => {
          this.remove(key);
        }, entry.removeDelay);
        entry.show = false;
        entry.removeDelay = -1;
      }
      this.fireEvent({
        type: 'remove',
        dialogs: this.dialogs,
        entry
      });
    }
  }

  /**
   * Adds a listener to the manager
   */
  addListener(l) {
    if (!this.listeners.includes(l)) {
      this.listeners.push(l);
    }
  }

  /**
   * Removes a listener from the manager
   */
  removeListener(l) {
    const idx = this.listeners.indexOf(l);
    if (idx >= 0) {
      this.listeners.splice(idx, 1);
    }
  }
  fireEvent(evt) {
    this.listeners.forEach(listener => {
      listener(evt);
    });
  }
}
const globalManager = new Manager();

/**
 * This is a manager of dynamic Dialogs. It will manage the show/hide of a dialog without 
 * having to embed it in your hierarchy. 
 * To use, call
 *   const myDialog = { component : MyDialogComponent, props : myDialogsProps, removeDelay : 500 }
 *   Dialogs.show( myDialog )
 * 
 * This can be invoked from anywhere. The removeDelay is optional and will default to 500ms.
 */

export default class Dialogs extends React.Component {
  /**
   * Static method to show a dialog. 
   * @param {object} dialog
   * @param {function} dialog.component the react component to create. This will be invoked with props
   * equals to { key, show, onClose }. The key is the react key. Show is true if the dialog is in a show
   * state, or false if it is being removed. onClose is a function to invoke when the dialog is done.
   * @param {object} dialog.props props to send to the component
   * @param {string} dialog.id if present, this will be used to prevent two dialogs with the same id from 
   * being shown. 
   * @param {number} dialog.removeDelay the time to delay between the recipt of the close and the time
   * the item is removed from rendering. This allows for exit animations to occur. (default is 500)
   * @returns (false or onClose}
   */
  static show(dialog) {
    return globalManager.add(dialog);
  }
  static hide(id) {
    return globalManager.removeById(id);
  }
  static hideAll() {
    return globalManager.removeAll();
  }
  static isShowing(id) {
    return globalManager.isShowing(id);
  }
  static getEntryById(id) {
    return globalManager.getById(id);
  }
  constructor(props) {
    super(props);
    this.state = {
      dialogs: []
    };
  }
  onChange = evt => {
    if (evt && evt.dialogs) {
      this.setState({
        dialogs: evt.dialogs
      });
    }
  };
  componentDidMount() {
    globalManager.addListener(this.onChange);
  }
  componentWillUnmount() {
    globalManager.removeListener(this.onChange);
  }
  render() {
    const {
      dialogs
    } = this.state;
    return dialogs.map(entry => {
      const {
        component: Comp,
        props,
        show,
        onClose,
        key
      } = entry;
      return /*#__PURE__*/_createElement(Comp, {
        ...props,
        key: key,
        show: show,
        onClose: onClose
      });
    });
  }
}